import { useState, useEffect, useRef, useContext } from "react"
import axios from 'axios'

import { Play, Check, X as XIcon, Minus } from "lucide-react"
import { loadPyodide } from 'pyodide'
import { record } from 'rrweb' 
import { X } from 'lucide-react';

import { EditorView, basicSetup } from "codemirror"
import { EditorState } from "@codemirror/state"
import { keymap } from "@codemirror/view"
import { indentWithTab } from "@codemirror/commands"
import { indentUnit } from "@codemirror/language"
import { python } from "@codemirror/lang-python"
import { autocompletion } from "@codemirror/autocomplete" 

import AIChat from '../chat/AIChat/AIChat'
import ConsentModal from './ConsentModal'

import { AuthContext } from '../../context/AuthContext'
import { SessionContext } from '../../context/SessionContext'
import { SelectedCodeContext } from '../../context/SelectedCodeContext'
import { TestResultContext } from '../../context/TestResultContext'

import { saveRrwebEvents } from '../../service/rrwebService'; 
import { addCodeAndUpdateUser } from '../../service/codeService'
import { checkConsent, submitConsent } from '../../service/consentService';
import { createSubmission, updateSubmission, createErr } from '../../service/submissionService'

import { getPassrateByResult } from '../../tool/progressUnit'

import '../../css/vizmental-ide.scss'
import { testsEnabled, hidesAiColumn } from '../session/sessionSettings'
import { studentTestView, testsInStudentOrder } from '../session/testVisibility'
import { StudentTestCell } from '../session/TestCaseIO'
import { completeTestResults, hiddenSafePythonRun, readPythonResult, upgradeLegacyPythonHarness } from '../session/testResults'

const DEFAULT_TERMINAL_OUTPUT = "Click 'Run Tests' to execute your code..."

export default function VizmentalIDE() {
  const { currentUser } = useContext(AuthContext)
  const { session } = useContext(SessionContext)
  const { 
    setSelectedCode, 
    currentCode, 
    setCurrentCode,
    keystrokes,
    setKeystrokes,
    setLatestCodeId,
    isIdle,
    setIsIdle
  } = useContext(SelectedCodeContext)
  const { testResult, setTestResult } = useContext(TestResultContext)

  // UI State
  const [leftPanelWidth, setLeftPanelWidth] = useState(450)
  const [rightPanelWidth, setRightPanelWidth] = useState(450)
  const [terminalHeight, setTerminalHeight] = useState(250)
  const [isDraggingHorizontal, setIsDraggingHorizontal] = useState(false)
  const [isDraggingVertical, setIsDraggingVertical] = useState(false)
  const [isDraggingChat, setIsDraggingChat] = useState(false)
  
  // Code execution state
  const [isRunning, setIsRunning] = useState(false)
  const [terminalOutput, setTerminalOutput] = useState(DEFAULT_TERMINAL_OUTPUT)
  const [testResults, setTestResults] = useState([])

  // Consent state
  const [showConsentModal, setShowConsentModal] = useState(false)
  const [hasConsented, setHasConsented] = useState(null)
  const [isProcessingConsent, setIsProcessingConsent] = useState(false)
  const [showDeclineMessage, setShowDeclineMessage] = useState(true);

  // Refs
  const containerRef = useRef(null)
  const editorRef = useRef(null)
  const editorViewRef = useRef(null)

  // Idle timer
  const [idleTimer, setIdleTimer] = useState(null)

  // Sync test results from context
  useEffect(() => {
    if (testResult) {
      setTestResults(testResult)
    }
  }, [testResult])

  // Check consent status
  useEffect(() => {
    const fetchConsent = async () => {
      if (!currentUser?.id || !session?.id) return
      
      try {
        const data = await checkConsent(currentUser.id, session.id)
        
        setHasConsented(data.hasConsented)
        
        if (data.hasConsented === false && data.consentedAt === null) {
          setShowConsentModal(true)
        }
      } catch (error) {
        console.error('Error checking consent:', error)
      }
    }

    fetchConsent()
  }, [currentUser?.id, session?.id])

  const handleConsent = async (consented) => {
    setIsProcessingConsent(true)
    
    try {
      await submitConsent(currentUser.id, session.id, consented)
      
      setHasConsented(consented)
      setShowConsentModal(false)
      
    } catch (error) {
      console.error('Error submitting consent:', error)
      alert('Failed to submit consent. Please try again.')
    } finally {
      setIsProcessingConsent(false)
    }
  }

  // Initialize CodeMirror editor
  useEffect(() => {
    if (editorRef.current && !editorViewRef.current && session) {
      const initialCode = currentCode || session?.test_code?.template || ''
      // currentCode (from SelectedCodeContext) defaults to null and only
      // gets set by the editor's own updateListener below, which fires on
      // the student's first edit - until then it stays null even though the
      // editor displays initialCode. runTests() reads currentCode directly,
      // and `null + '\n' + ...` JS-coerces to the literal text "null" as the
      // first line of the Python source, so clicking Run Tests before typing
      // anything threw NameError: name 'null' is not defined.
      if (!currentCode && initialCode) {
        setCurrentCode(initialCode)
      }

      const state = EditorState.create({
        doc: initialCode,
        extensions: [
          basicSetup,
          python(),
          indentUnit.of("    "),
          keymap.of([indentWithTab]),
          autocompletion({ override: [] }),
          EditorView.updateListener.of((update) => {
            if (update.docChanged) {
              const newCode = update.state.doc.toString()
              setCurrentCode(newCode)
              
              // Track keystrokes
              const changes = update.changes
              if (changes) {
                setKeystrokes(prev => [...prev, {
                  time: new Date(),
                  changes: changes.toJSON()
                }])
              }
            }
          }),
         EditorView.theme({
            "&": {
                height: "100%",
                fontSize: "14px",
                color: "#1f2937", // gray-800
            },
            ".cm-content": {
                padding: "16px",
                minHeight: "100%",
                color: "#1f2937",
            },
            ".cm-focused": {
                outline: "none",
            },
            ".cm-editor": {
                height: "100%",
                backgroundColor: "#ffffff", // pure white
            },
            ".cm-scroller": {
                height: "100%",
            },
            ".cm-line": {
                color: "#1f2937",
            },
            ".cm-cursor": {
                borderLeftColor: "#1f2937",
            },
            ".cm-selectionBackground": {
                backgroundColor: "#e5e7eb", // light gray selection
            },
            ".cm-keyword": {
                color: "#7c3aed", // purple
            },
            ".cm-string": {
                color: "#059669", // green
            },
            ".cm-comment": {
                color: "#6b7280", // gray
            },
            ".cm-number": {
                color: "#dc2626", // red
            },
            }),
        ],
      })

      editorViewRef.current = new EditorView({
        state,
        parent: editorRef.current,
      })
    }

    return () => {
      if (editorViewRef.current) {
        editorViewRef.current.destroy()
        editorViewRef.current = null
      }
    }
  }, [session])

  // Idle detection
  useEffect(() => {
    if (idleTimer) {
      clearTimeout(idleTimer)
    }

    if (!isIdle) {
      const newTimer = setTimeout(() => {
        setIsIdle(true)
      }, 2 * 60 * 1000)
      setIdleTimer(newTimer)
    }

    return () => {
      if (idleTimer) {
        clearTimeout(idleTimer)
      }
    }
  }, [currentCode, isIdle])

  // Rrweb recording - only if consented
  useEffect(() => {
    if (!session?.id || !currentUser?.id || hasConsented !== true) return
    
    const eventsBuffer = []
    
    const stopRecording = record({
      emit(event) {
        eventsBuffer.push(event)
      }
    })
    
    // Send events to backend every 10 seconds
    const flushInterval = setInterval(() => {
      if (eventsBuffer.length > 0) {
        console.log(`📤 Sending ${eventsBuffer.length} events`)
        
        saveRrwebEvents(session.id, currentUser.id, eventsBuffer) 
          .then(() => {
            console.log('✅ Events saved')
            eventsBuffer.length = 0
          })
          .catch(err => {
            console.error('❌ Failed to save events:', err)
          })
      }
    }, 10000)
    
    return () => {
      stopRecording()
      clearInterval(flushInterval)
      
      if (eventsBuffer.length > 0) {
        console.log(`📤 Sending final ${eventsBuffer.length} events`)
        saveRrwebEvents(session.id, currentUser.id, eventsBuffer)
          .catch(err => console.error('Failed to save final events:', err))
      }
    }
  }, [session?.id, currentUser?.id, hasConsented])

  // Run tests function
  const runTests = async () => {
  if (isRunning || !session) return
  
  setIsRunning(true)
  // Set once this run's results are shown, so a later failure (e.g. while saving) is not
  // mistaken below for a run that never happened.
  let resultsShown = false

  try {
    // With test cases off (session.enable_tests === false) Run executes only the student's
    // code: no variable-setup starter, no hidden harness, and no `result` global read back
    // as test results - otherwise a student's own top-level list named `result` would be
    // scored as a pass rate.
    const hasTests = testsEnabled(session)
    const starterCode = hasTests ? session?.test_code?.starter || '' : ''
    const unitTest = hasTests ? upgradeLegacyPythonHarness(session?.test_code?.unit_test || '') : ''
    let pythonResult = []

    const pyodide = await loadPyodide({
      indexURL: 'https://cdn.jsdelivr.net/pyodide/v0.23.4/full/',
    })
    // Pyodide's base runtime doesn't include numpy until it's explicitly
    // loaded - without this, any exercise whose starter/solution code does
    // `import numpy` fails with a ModuleNotFoundError.
    await pyodide.loadPackage('numpy')

    let pythonStdOut = ''
    pyodide.setStdout({
      isatty: false,
      batched: (output) => {
        pythonStdOut += output + '\n'
      },
    })

    let pythonStdErr = ''
    let passrate = 0

    // Tests on: one pass/fail per test after the run, success or error - tests the harness
    // never reached (syntax error, top-level exception) count as failed. The shared context
    // (read by the AI tutor) and the local table get the same padded array, which is also
    // what the pass rate and submission below use.
    const collectTestResults = () => {
      if (!hasTests) return
      pythonResult = completeTestResults(readPythonResult(pyodide.globals), session.test_list?.length)
      setTestResult(pythonResult)
      setTestResults(pythonResult)
      passrate = getPassrateByResult(pythonResult)
      resultsShown = true
    }

    try {
      // Hidden tests stay out of the student's code's reach (no test-data globals, no output
      // while they run) wherever the setup and harness are the generated ones; hand-written
      // ones run as saved (testResults.hiddenSafePythonRun).
      const safeRun = hasTests
        ? hiddenSafePythonRun({ starterCode, unitTest: session?.test_code?.unit_test || '', testList: session?.test_list })
        : null
      await pyodide.runPython(safeRun ? safeRun.source(currentCode) : currentCode + '\n' + starterCode + '\n' + unitTest)

      // Get test results
      collectTestResults()

      // Format test results for terminal
      if (pythonResult && pythonResult.length > 0) {
            const passedCount = pythonResult.filter(r => r === true).length
            const totalCount = pythonResult.length
            
            let terminalOutput = `Successfully Ran: ${passedCount}/${totalCount} passed`
            
            // If there's any print output, show it below
            if (pythonStdOut.trim()) {
                terminalOutput += `\n\n--- Output ---\n${pythonStdOut}`
        }
  
    setTerminalOutput(terminalOutput)
    } else {
    setTerminalOutput(pythonStdOut || 'Code executed successfully')
    }
      
    } catch (err) {
      pythonStdErr = err.toString()
      
      let indexE = pythonStdErr.lastIndexOf('<exec>')
      let indexM = pythonStdErr.lastIndexOf('<module>')
      let index = Math.max(indexE, indexM)
      
      if (index === indexE && index !== -1) {
        setTerminalOutput(pythonStdErr.substring(index - 6))
      } else if (index === indexM && index !== -1) {
        setTerminalOutput(pythonStdErr.substring(index + 9))
      } else {
        setTerminalOutput(pythonStdErr)
      }
      
      // Still get test results even if there was an error
      collectTestResults()
    }

    if (currentUser) {
      const result = await addCodeAndUpdateUser(
        currentCode,
        passrate,
        currentUser.id,
        keystrokes
      )
      
      if (result.success) {
        setSelectedCode(result.code)
        setLatestCodeId(result.code.id)
        
        const submission = createSubmission(
          currentUser.id,
          result.code.id,
          session.id,
          pythonResult,
          passrate,
          createErr(pythonStdErr)
        )
        await updateSubmission(submission)
      }
    }
  } catch (error) {
    console.error('Error running code:', error)
    setTerminalOutput('Error executing code: ' + error.message)
    // The runner itself failed (e.g. Pyodide did not load): no test ran, so with tests on
    // every row ends as failed instead of keeping the previous run's icons. Nothing is saved.
    if (!resultsShown && testsEnabled(session)) {
      const failed = completeTestResults([], session.test_list?.length)
      setTestResult(failed)
      setTestResults(failed)
    }
  } finally {
    setIsRunning(false)
  }
}

  // Pass/Fail Icon Component
const PassIcon = ({ testResult }) => {
  if (testResult == null) {
    return (
      <div className="test-icon test-icon-pending">
        <Minus className="w-5 h-5" />
      </div>
    )
  }
  if (testResult) {
    return (
      <div className="test-icon test-icon-passed">
        <Check className="w-5 h-5" />
      </div>
    )
  } else {
    return (
      <div className="test-icon test-icon-failed">
        <XIcon className="w-5 h-5" />
      </div>
    )
  }
}

  // Resizing handlers
  const handleMouseDownHorizontal = (e) => {
    e.preventDefault()
    setIsDraggingHorizontal(true)
  }

  const handleMouseDownVertical = (e) => {
    e.preventDefault()
    setIsDraggingVertical(true)
  }

  const handleMouseDownChat = (e) => {
    e.preventDefault()
    setIsDraggingChat(true)
  }

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDraggingHorizontal && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect()
        const newWidth = Math.max(300, Math.min(800, e.clientX - containerRect.left))
        setLeftPanelWidth(newWidth)
      }
      if (isDraggingVertical && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect()
        const newHeight = Math.max(100, containerRect.bottom - e.clientY)
        setTerminalHeight(newHeight)
      }
      if (isDraggingChat && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect()
        const newWidth = Math.max(300, Math.min(800, containerRect.right - e.clientX))
        setRightPanelWidth(newWidth)
      }
    }

    const handleMouseUp = () => {
      setIsDraggingHorizontal(false)
      setIsDraggingVertical(false)
      setIsDraggingChat(false)
    }

    if (isDraggingHorizontal || isDraggingVertical || isDraggingChat) {
      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("mouseup", handleMouseUp)
      document.body.style.cursor = isDraggingHorizontal || isDraggingChat ? "col-resize" : "row-resize"
      document.body.style.userSelect = "none"
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
      document.body.style.cursor = ""
      document.body.style.userSelect = ""
    }
  }, [isDraggingHorizontal, isDraggingVertical, isDraggingChat])

  // AI column: hidden while AI chat is off (sessionSettings.hidesAiColumn). AIChat is
  // mounted the first time the column is shown and afterwards only hidden, never unmounted,
  // so switching chat off and back on mid-class keeps the assistant's conversation memory.
  const showAiPanel = hasConsented === true && Boolean(session) && !hidesAiColumn(session)
  const [aiPanelMounted, setAiPanelMounted] = useState(false)
  useEffect(() => {
    if (showAiPanel) setAiPanelMounted(true)
  }, [showAiPanel])

  if (!session) {
    return (
      <div className="vizmental-loading">
        <div className="loading-spinner"></div>
        <p>Loading IDE...</p>
      </div>
    )
  }

  const hasTests = testsEnabled(session)
  const runLabel = hasTests ? 'Run Tests' : 'Run'

  return (
    <div className="vizmental-container">
      {/* Consent Modal */}
      {showConsentModal && (
        <ConsentModal
          isOpen={showConsentModal}
          onConsent={handleConsent}
          isProcessing={isProcessingConsent}
        />
      )}

      {/* Show message if declined */}
      {hasConsented === false && showDeclineMessage && (
      <div style={{
        position: 'fixed',
        top: '80px',
        left: '50%',
        transform: 'translateX(-50%)',
        padding: '16px',
        paddingRight: '40px',
        backgroundColor: '#f3f4f6',
        border: '1px solid #d1d5db',
        borderRadius: '8px',
        maxWidth: '600px',
        zIndex: 1000,
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
      }}>
        <button
          onClick={() => setShowDeclineMessage(false)}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#6b7280'
          }}
          aria-label="Close"
        >
          <X size={18} />
        </button>
        <p style={{ margin: 0, fontSize: '14px', color: '#374151' }}>
          You declined to participate in the research study. You can still complete the assignment, 
          but AI assistance and screen recording are not available.
        </p>
      </div>
    )}

      {/* Main Content Area */}
      <div className="vizmental-content" ref={containerRef}>
        {/* Left Panel - Problem Description & Test Cases */}
        <div className="vizmental-left-panel" style={{ width: `${leftPanelWidth}px` }}>
          <div className="panel-content">
            {/* Task Description */}
            <div className="content-section">
              <h3 className="section-title">Task Description</h3>
              <div className="description-box">
                {session.task || 'Complete the programming task.'}
              </div>
            </div>
            
            {/* Test Cases Table */}
            {hasTests && session.test_list && session.test_list.length > 0 && (
              <>
                <div className="section-divider"></div>
                <div className="content-section">
                  <h3 className="section-title">Test Cases</h3>
                  <div className="test-table-wrapper">
                    <table className="test-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th className="result-column">Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {testsInStudentOrder(session.test_list).map(({ index }) => {
                          // Public tests first, as "Example #N" with their input and
                          // expected output (testVisibility.js); hidden ones render as they
                          // always have. `index` is the row's place in test_list, which the
                          // results are matched by.
                          const view = studentTestView(session.test_list, index, `Test ${index + 1}`)
                          return (
                          <tr key={index}>
                            <td>
                              <StudentTestCell view={view} />
                            </td>
                            <td className="result-column">
                              <PassIcon testResult={testResults[index]} />
                            </td>
                          </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Horizontal Resizer */}
        <div
          className="vizmental-resizer horizontal"
          onMouseDown={handleMouseDownHorizontal}
        />

        {/* Middle Panel - Code Editor & Terminal */}
        <div className="vizmental-middle-panel">
          <div className="editor-container">
            <div ref={editorRef} className="code-editor" />
          </div>

          {/* Terminal/Output Panel */}
          <div className="terminal-panel" style={{ height: `${terminalHeight}px` }}>
            <div
              className="vizmental-resizer vertical"
              onMouseDown={handleMouseDownVertical}
            />

            <div className="terminal-controls">
              <div className="controls-left">
                <button
                  onClick={runTests}
                  disabled={isRunning}
                  className="run-button"
                >
                  <Play className="w-3 h-3" />
                  {isRunning ? 'Running...' : runLabel}
                </button>
              </div>
            </div>

            <div className="terminal-output">
              <div className="output-content">
                {!hasTests && terminalOutput === DEFAULT_TERMINAL_OUTPUT
                  ? "Click 'Run' to execute your code..."
                  : terminalOutput}
              </div>
            </div>
          </div>
        </div>

        {/* Chat Resizer - only show if consented and the AI pane is on */}
        {showAiPanel && (
          <div
            className="vizmental-resizer horizontal"
            onMouseDown={handleMouseDownChat}
          />
        )}

        {/* Right Panel - AI Chat - only for consented students, and only visible while
            the AI pane is on; once mounted it is hidden rather than unmounted. */}
        {hasConsented === true && aiPanelMounted && (
          <div
            className="vizmental-right-panel"
            style={showAiPanel ? { width: `${rightPanelWidth}px` } : { display: 'none' }}>
            <AIChat />
          </div>
        )}
      </div>
    </div>
  )
}